import React from "react";
import "../css/pagination.scss";
import SelectOption from "./SelectOption";
import { useTranslation } from "../contexts/LocaleContext";

const PaginationFooter = ({
  pageSize,
  onPageSizeChange,
  currentPage,
  onPageChange,
  totalPages,
  goToValue,
  onGoToValueChange,
  onGoClick,
}) => {
  const { t } = useTranslation();
  // compute page buttons (max 4 numeric buttons)
  const pages = [];
  if (totalPages <= 4) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    if (currentPage <= 4) {
      pages.push(1, 2, 3, 4);
    } else if (currentPage >= totalPages - 3) {
      for (let p = totalPages - 3; p <= totalPages; p++) pages.push(p);
    } else {
      // center current page within 4 slots
      pages.push(
        currentPage - 1,
        currentPage,
        currentPage + 1,
        currentPage + 2
      );
    }
  }

  return (
    <div className="pagination-container">
      <div className="page-size-wrap">
        <SelectOption
          value={pageSize}
          onChange={(v) => onPageSizeChange(Number(v))}
          options={[5, 10, 25, 50, 100].map((n) => ({
            value: n,
            label: t ? t("pagination.per_page", { n }) : `${n} / page`,
          }))}
          ariaLabel="page size"
        />
      </div>

      <div className="pagination-controls">
        <button
          className="prev-button"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1em"
            height="1em"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M15 18l-6-6 6-6"
              stroke="#000"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="page-list">
          {/* leading first page + ellipsis if needed */}
          {pages.length > 0 && pages[0] > 1 && (
            <>
              <button
                key="first"
                onClick={() => onPageChange(1)}
                className="page-button"
              >
                1
              </button>
              {pages[0] > 2 && <span className="ellipsis">...</span>}
            </>
          )}

          {pages.map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`page-button ${
                pageNum === currentPage ? "active" : ""
              }`}
            >
              {pageNum}
            </button>
          ))}

          {/* trailing ellipsis + last page if needed */}
          {pages.length > 0 && pages[pages.length - 1] < totalPages && (
            <>
              {pages[pages.length - 1] < totalPages - 1 && (
                <span className="ellipsis">...</span>
              )}
              <button
                key="last"
                onClick={() => onPageChange(totalPages)}
                className="page-button"
              >
                {totalPages}
              </button>
            </>
          )}
        </div>

        <button
          className="next-button"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1em"
            height="1em"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M9 6l6 6-6 6"
              stroke="#000"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="go-to">
        <label style={{ color: "#6b7280" }}>
          {t ? t("pagination.go_to") : "Go to"}
        </label>
        <input
          className="go-input"
          type="number"
          min={1}
          max={totalPages}
          value={goToValue}
          onChange={(e) => onGoToValueChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onGoClick();
          }}
        />
        <div className="page-info">{`${currentPage} / ${totalPages}`}</div>
      </div>
    </div>
  );
};

export default PaginationFooter;
